'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import Link from 'next/link'

import { Container } from '@/components/ui/container'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { trackCta } from '@/lib/analytics/track-cta'
import { CheckIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx/lite'
import type { ComponentProps, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

type PricingPlan = {
  name: string
  badge?: string
  includedUsers: number
  basePrice: number
  perUserPrice: number
  features: string[]
  ctaVariant: 'primary' | 'soft'
  ctaHref: string
}

type PricingSliderProps = {
  headline: ReactNode
  subheadline: ReactNode
  locale: string
  currency: string
  slider: {
    label: string
    min: number
    max: number
    maxLabel: string
    unitSingular: string
    unitPlural: string
  }
  billing: {
    perMonthLabel: string
    baseLabel: string
    perUserLabel: string
    includedLabel: string
  }
  planCtaLabel: string
  contactCtaLabel: string
  plans: PricingPlan[]
  contactHref: string
} & Omit<ComponentProps<'section'>, 'children'>

// In light mode the near-black `primary` pill reads as the recommended plan against the pale `soft`
// one. In dark mode that contrast collapsed — a light-grey pill beside a translucent-white one, both
// muted — so the one signal that says "this is the plan most firms pick" was gone exactly where the
// conversion sits. Dark mode uses the brand accent instead, the same blue the nav CTA carries.
const buttonStyles = {
  // Same reason as `button.tsx`: white on the brand blue is 3.32:1 at this size, where AA asks
  // 4.5:1. `accent-strong` carries white at 4.76:1 and `accent-deep` at 5.40:1.
  primary: 'bg-mist-950 text-white hover:bg-mist-800 dark:bg-accent-strong dark:text-white dark:hover:bg-accent-deep',
  soft: 'bg-mist-950/10 text-mist-950 hover:bg-mist-950/15 dark:bg-white/10 dark:text-white dark:hover:bg-white/20',
}

function usePrefersReducedMotion() {
  return useSyncExternalStore(
    (callback) => {
      if (typeof window === 'undefined') {
        return () => {}
      }
      const media = window.matchMedia('(prefers-reduced-motion: reduce)')
      media.addEventListener('change', callback)
      return () => media.removeEventListener('change', callback)
    },
    () => (typeof window === 'undefined' ? false : window.matchMedia('(prefers-reduced-motion: reduce)').matches),
    () => false,
  )
}

function useAnimatedNumber(value: number, duration = 350) {
  const [display, setDisplay] = useState(value)
  const lastDisplayRef = useRef(value)
  const rafRef = useRef<number | null>(null)
  const prefersReduced = usePrefersReducedMotion()

  useEffect(() => {
    lastDisplayRef.current = display
  }, [display])

  useEffect(() => {
    if (value === lastDisplayRef.current) {
      return
    }

    if (prefersReduced) {
      return
    }

    const from = lastDisplayRef.current
    const diff = value - from
    const start = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress
      setDisplay(from + diff * eased)
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [duration, prefersReduced, value])

  return prefersReduced ? value : display
}

export function PricingSlider({
  headline,
  subheadline,
  locale,
  currency,
  slider,
  billing,
  planCtaLabel,
  contactCtaLabel,
  plans,
  contactHref,
  className,
  ...props
}: PricingSliderProps) {
  const [userCount, setUserCount] = useState(slider.min)
  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale, currency],
  )

  const isMax = userCount >= slider.max
  const displayCount = isMax ? slider.maxLabel : String(userCount)
  const unitLabel = userCount === 1 ? slider.unitSingular : slider.unitPlural

  const formatIncludedLabel = (count: number) =>
    billing.includedLabel.includes('{count}')
      ? billing.includedLabel.replace('{count}', String(count))
      : billing.includedLabel

  return (
    <section className={clsx('pb-16', className)} {...props}>
      {/* The same wallpaper the product pages open on, carrying the heading this page already had. */}
      <div className="px-2 pb-10 sm:pb-14">
        <div
          className="relative overflow-hidden rounded-lg px-6 py-12 text-white sm:px-10 sm:py-16"
          style={{ background: 'var(--wp-1)' }}
        >
          <Container className="flex flex-col items-center gap-6 text-center">
            <Heading className="text-white">{headline}</Heading>
            <Text size="lg" className="max-w-xl text-white/75">
              {subheadline}
            </Text>
          </Container>
        </div>
      </div>

      <Container className="flex flex-col gap-10 sm:gap-16">
        <div className="w-full text-sm/6 text-mist-700 lg:mx-auto lg:max-w-sm xl:max-w-md dark:text-mist-300">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-mist-950 dark:text-white">{slider.label}</p>
              <div>
                {isMax ? (
                  <span className="font-semibold text-mist-950 dark:text-white">{contactCtaLabel}</span>
                ) : (
                  <>
                    <span className="font-semibold text-mist-950 dark:text-white">{displayCount}</span> {unitLabel}
                  </>
                )}
              </div>
            </div>
            <input
              type="range"
              min={slider.min}
              max={slider.max}
              value={userCount}
              onChange={(event) => setUserCount(Number(event.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-mist-950/10 accent-mist-950 dark:bg-white/15 dark:accent-white [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-mist-950 dark:[&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-mist-950 dark:[&::-webkit-slider-thumb]:bg-white"
              aria-label={slider.label}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {plans.map((plan) => (
            <PricingPlanCard
              key={plan.name}
              plan={plan}
              userCount={userCount}
              maxUsers={slider.max}
              billing={billing}
              formatter={formatter}
              formatIncludedLabel={formatIncludedLabel}
              planCtaLabel={planCtaLabel}
              contactCtaLabel={contactCtaLabel}
              contactHref={contactHref}
            />
          ))}
        </div>
      </Container>
    </section>
  )
}

function PricingPlanCard({
  plan,
  userCount,
  maxUsers,
  billing,
  formatter,
  formatIncludedLabel,
  planCtaLabel,
  contactCtaLabel,
  contactHref,
}: {
  plan: PricingPlan
  userCount: number
  maxUsers: number
  billing: PricingSliderProps['billing']
  formatter: Intl.NumberFormat
  formatIncludedLabel: (count: number) => string
  planCtaLabel: string
  contactCtaLabel: string
  contactHref: string
}) {
  const extraUsers = Math.max(userCount - plan.includedUsers, 0)
  const totalPrice = plan.basePrice + extraUsers * plan.perUserPrice
  const animatedTotalPrice = useAnimatedNumber(totalPrice)
  const isMax = userCount >= maxUsers
  const ctaLabel = isMax ? contactCtaLabel : planCtaLabel
  const ctaHref = isMax ? contactHref : plan.ctaHref

  return (
    <div className="flex h-full flex-col justify-between gap-6 rounded-xl bg-mist-950/2.5 p-6 text-mist-950 sm:items-start dark:bg-white/5 dark:text-white">
      <div className="self-stretch">
        <div className="flex items-center justify-between">
          {/* `h2`, not `h3`. The only heading above a plan card is the section's own `h1`, so an
              `h3` skipped a level — Lighthouse's `heading-order`, and a screen-reader user
              tabbing the outline lands on a level that has no parent. */}
          <h2 className="text-2xl/8 tracking-tight">{plan.name}</h2>
          {plan.badge && (
            <span className="rounded-full bg-mist-950/10 px-2 text-xs/6 font-medium text-mist-950 dark:bg-white/10 dark:text-white">
              {plan.badge}
            </span>
          )}
        </div>

        {isMax ? (
          <p className="mt-3 text-sm/6 text-mist-500 dark:text-mist-400">{contactCtaLabel}</p>
        ) : (
          <p className="mt-3 inline-flex items-baseline gap-2 text-base/7">
            <span className="text-3xl font-semibold text-mist-950 dark:text-white">
              {formatter.format(animatedTotalPrice)}
            </span>
            <span className="text-sm/6 text-mist-500 dark:text-mist-400">{billing.perMonthLabel}</span>
          </p>
        )}

        <div className="mt-3 space-y-1 text-xs/6 text-mist-500 dark:text-mist-400">
          <p>
            {formatter.format(plan.basePrice)} {billing.baseLabel} · {formatIncludedLabel(plan.includedUsers)}
          </p>
          <p>
            {formatter.format(plan.perUserPrice)} {billing.perUserLabel}
          </p>
        </div>

        <ul className="mt-4 space-y-2 text-sm/6 text-mist-700 dark:text-mist-400">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex gap-4">
              <CheckIcon className="h-lh shrink-0 stroke-mist-950 dark:stroke-white" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <Link
        href={ctaHref}
        onClick={() => trackCta(isMax ? 'pricing-contact' : `pricing-plan-${plan.ctaVariant}`, ctaLabel)}
        className={clsx(
          'inline-flex items-center justify-center rounded-full px-4 py-2 text-sm/7 font-medium',
          buttonStyles[plan.ctaVariant],
        )}
      >
        {ctaLabel}
      </Link>
    </div>
  )
}
